import {
  VEGETATION_ASSETS,
  VEGETATION_ASSET_IDS,
  VEGETATION_OBJECTS,
  type VegetationAssetId,
} from "@endclock/shared";
import { loadStaticGlb } from "../assets/gltf.js";
import type { Camera } from "../engine/camera.js";
import { type GpuMesh, type Renderer, type StaticRenderMaterial } from "../engine/renderer.js";
import { ReusableInstanceBatch, createStaticObjectChunks } from "../world/static-instances.js";

interface VegetationManifestEntry {
  readonly id: VegetationAssetId;
  readonly url: string;
  readonly triangleCount: number;
  readonly primitiveCount: number;
  readonly alphaCutout: boolean;
  readonly collision: { readonly shape: "none" | "circle"; readonly radius: number };
  readonly boundsCenterY: number;
  readonly boundsRadius: number;
}

interface VegetationManifest {
  readonly version: number;
  readonly format: string;
  readonly textures: readonly {
    readonly url: string;
    readonly width: number;
    readonly height: number;
    readonly filtering: "nearest";
    readonly alpha: "mask";
  }[];
  readonly assets: readonly VegetationManifestEntry[];
}

interface LoadedVegetationPrimitive {
  readonly mesh: GpuMesh;
  readonly baseColor: readonly [number, number, number, number];
  readonly material: StaticRenderMaterial;
  readonly batch: ReusableInstanceBatch;
}

interface LoadedVegetationAsset {
  readonly primitives: readonly LoadedVegetationPrimitive[];
  readonly boundsCenterY: number;
  readonly boundsRadius: number;
  readonly triangleCount: number;
}

export type VegetationLoadState = "loading" | "ready" | "failed";

export class VegetationWorld {
  objects: readonly (typeof VEGETATION_OBJECTS)[number][] = VEGETATION_OBJECTS;
  state: VegetationLoadState = "loading";
  loadError: string | undefined;
  private readonly assets = new Map<VegetationAssetId, LoadedVegetationAsset>();
  private chunks = createStaticObjectChunks(this.objects, (object) => {
    const definition = VEGETATION_ASSETS[object.assetId];
    return {
      centerY: object.y + definition.boundsCenterY * object.scale,
      radius: definition.boundsRadius * object.scale,
    };
  });

  constructor(private readonly renderer: Renderer) {
    renderer.metrics.totalVegetationTrees = this.objects.filter((object) => !isGroundVegetation(object.category)).length;
    renderer.metrics.totalVegetationShrubs = this.objects.filter((object) => isGroundVegetation(object.category)).length;
    void this.load();
  }

  setObjects(objects: readonly (typeof VEGETATION_OBJECTS)[number][]): void {
    this.renderer.invalidateLocalShadowCache();
    this.objects = objects;
    this.chunks = createStaticObjectChunks(objects, (object) => {
      const definition = VEGETATION_ASSETS[object.assetId];
      return {
        centerY: object.y + definition.boundsCenterY * object.scale,
        radius: definition.boundsRadius * object.scale,
      };
    });
    this.renderer.metrics.totalVegetationTrees = objects.filter((object) => !isGroundVegetation(object.category)).length;
    this.renderer.metrics.totalVegetationShrubs = objects.filter((object) => isGroundVegetation(object.category)).length;
    if (this.state === "ready") {
      this.renderer.metrics.totalVegetationTriangles = objects.reduce(
        (sum, object) => sum + (this.assets.get(object.assetId)?.triangleCount ?? 0),
        0,
      );
    }
  }

  render(camera: Camera): void {
    if (this.state !== "ready") return;
    for (const asset of this.assets.values()) {
      for (const primitive of asset.primitives) primitive.batch.reset();
    }
    for (const chunk of this.chunks) {
      if (!camera.isSphereVisible(chunk.centerX, chunk.centerY, chunk.centerZ, chunk.radius)) continue;
      for (const object of chunk.objects) {
        const asset = this.assets.get(object.assetId);
        if (!asset) continue;
        const horizontalDistance = Math.hypot(object.x - camera.position[0], object.z - camera.position[2]);
        if (horizontalDistance > vegetationVisibilityDistance(object.category)) continue;
        const centerY = object.y + asset.boundsCenterY * object.scale;
        if (!camera.isSphereVisible(object.x, centerY, object.z, asset.boundsRadius * object.scale)) continue;
        for (const primitive of asset.primitives) {
          primitive.batch.pushUpright(object, primitive.baseColor, 0.055, 0.48);
        }
        if (isGroundVegetation(object.category)) {
          this.renderer.metrics.visibleVegetationShrubs += 1;
        }
        else this.renderer.metrics.visibleVegetationTrees += 1;
      }
    }
    for (const assetId of VEGETATION_ASSET_IDS) {
      const asset = this.assets.get(assetId);
      if (!asset) continue;
      for (const primitive of asset.primitives) {
        this.renderer.drawStaticInstanced(
          primitive.mesh,
          primitive.batch.data,
          primitive.batch.count,
          primitive.material,
          "vegetation",
        );
      }
    }
  }

  private async load(): Promise<void> {
    try {
      const response = await fetch("/assets/vegetation/manifest.json");
      if (!response.ok) throw new Error("Vegetation manifest request failed");
      const manifest = (await response.json()) as VegetationManifest;
      if (manifest.version !== 1 || manifest.assets.length !== VEGETATION_ASSET_IDS.length) {
        throw new Error("Invalid vegetation manifest");
      }
      if (
        manifest.textures.length !== 8 ||
        manifest.textures.some(
          (texture) => texture.width > 128 || texture.height > 128 || texture.filtering !== "nearest" || texture.alpha !== "mask",
        )
      ) {
        throw new Error("Invalid vegetation texture budget");
      }
      const loaded = await Promise.all(
        manifest.assets.map(async (entry) => {
          const definition = VEGETATION_ASSETS[entry.id];
          if (
            !definition ||
            Math.abs(definition.collisionRadius - entry.collision.radius) > 0.0001 ||
            Math.abs(definition.boundsCenterY - entry.boundsCenterY) > 0.0001 ||
            Math.abs(definition.boundsRadius - entry.boundsRadius) > 0.0001
          ) {
            throw new Error(`Vegetation metadata mismatch: ${entry.id}`);
          }
          const asset = await loadStaticGlb(entry.url);
          const triangleCount = asset.primitives.reduce((sum, primitive) => sum + primitive.geometry.indices.length / 3, 0);
          const hasMask = asset.primitives.some((primitive) => primitive.material.alphaMode === "MASK");
          if (
            asset.id !== entry.id ||
            triangleCount !== entry.triangleCount ||
            asset.primitives.length !== entry.primitiveCount ||
            hasMask !== entry.alphaCutout ||
            asset.collision.shape !== entry.collision.shape
          ) {
            throw new Error(`Vegetation asset mismatch: ${entry.id}`);
          }
          return [entry.id, asset, triangleCount] as const;
        }),
      );

      let totalTriangles = 0;
      for (const [id, asset, triangleCount] of loaded) {
        const definition = VEGETATION_ASSETS[id];
        const primitives: LoadedVegetationPrimitive[] = [];
        for (const primitive of asset.primitives) {
          const texture = primitive.material.baseColorTexture
            ? await this.renderer.loadTexture(primitive.material.baseColorTexture.uri)
            : undefined;
          if (primitive.material.alphaMode === "MASK" && (!texture || primitive.material.alphaCutoff !== 0.5)) {
            throw new Error(`Vegetation alpha material mismatch: ${id}`);
          }
          const mesh = this.renderer.createMesh(primitive.geometry);
          this.renderer.metrics.vegetationBufferBytes += mesh.bufferBytes;
          primitives.push({
            mesh,
            baseColor: primitive.material.baseColor,
            material: {
              ...(texture ? { texture } : {}),
              alphaCutoff: primitive.material.alphaCutoff,
              doubleSided: primitive.material.doubleSided,
            },
            batch: new ReusableInstanceBatch(),
          });
        }
        if (
          !isGroundVegetation(definition.category) &&
          !asset.primitives.some((primitive) => primitive.material.alphaMode === "OPAQUE")
        ) {
          throw new Error(`Tree lacks 3D trunk primitive: ${id}`);
        }
        this.assets.set(id, {
          primitives,
          boundsCenterY: asset.boundsCenterY,
          boundsRadius: asset.boundsRadius,
          triangleCount,
        });
      }
      for (const object of this.objects) totalTriangles += this.assets.get(object.assetId)!.triangleCount;
      this.renderer.metrics.totalVegetationTriangles = totalTriangles;
      this.state = "ready";
    } catch (error) {
      this.state = "failed";
      this.loadError = error instanceof Error ? error.message : "Unknown vegetation load error";
      console.error(this.loadError);
    }
  }
}

function isGroundVegetation(category: string): boolean {
  return ["dead_shrub", "living_shrub", "grass", "understory"].includes(category);
}

function vegetationVisibilityDistance(category: string): number {
  if (category === "grass" || category === "understory") return 52;
  if (category === "dead_shrub" || category === "living_shrub") return 72;
  if (category === "fallen_log") return 96;
  if (category === "nuclear_tree" || category === "dead_giant") return 190;
  return 138;
}
